/** Thin fetch wrapper around the FastAPI backend. */

// i18n imports only the Locale *type* from this module, so no runtime cycle.
import { INTL_LOCALES, t } from './i18n'

export class ApiError extends Error {
  status: number
  constructor(status: number, message: string) {
    super(message)
    this.status = status
    this.name = 'ApiError'
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    // Send the httpOnly session cookie on admin calls.
    credentials: 'include',
    ...init,
    headers: {
      ...(init.body && !(init.body instanceof FormData)
        ? { 'Content-Type': 'application/json' }
        : {}),
      ...init.headers,
    },
  })

  if (!response.ok) {
    let detail = response.statusText
    try {
      const body = await response.json()
      if (typeof body.detail === 'string') detail = body.detail
      else if (Array.isArray(body.detail))
        detail = body.detail
          .map((d: { msg: string; loc?: (string | number)[] }) => {
            const field = Array.isArray(d.loc) ? String(d.loc[d.loc.length - 1]) : ''
            return field && field !== 'body' ? `${field}: ${d.msg}` : d.msg
          })
          .join(', ')
    } catch {
      // Non-JSON error body - keep the status text.
    }
    throw new ApiError(response.status, detail)
  }

  if (response.status === 204) return undefined as T
  return response.json() as Promise<T>
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  upload: <T>(path: string, file: File) => {
    const form = new FormData()
    // Always pass an explicit filename: iOS Safari can otherwise send the
    // part without one, and the server then cannot recognise it as a file.
    form.append('file', file, file.name || 'foto.jpg')
    return request<T>(path, { method: 'POST', body: form })
  },
}

// --- Types mirroring the backend schemas ------------------------------------

export type Locale = 'de' | 'en' | 'ja' | 'ko' | 'zh'
export type ProductStatus = 'available' | 'reserved' | 'sold'
export type BookingMode = 'request' | 'live' | 'both'

export interface ContentBlock {
  type: 'hero' | 'heading' | 'text' | 'blurb' | 'image' | 'list'
  /** Heading depth (1-4) on pages converted from the migrated legal HTML. */
  level?: number
  /** Bullet-list items. */
  items?: string[]
  /** Prose that renders exactly as written - no eyebrow/heading heuristics. */
  plain?: boolean
  /** Optional corner-ribbon text on a blurb card (e.g. "Am selben Tag"). */
  badge?: string
  eyebrow?: string
  headline?: string
  cta?: string
  /** Where the hero button leads - an in-page anchor or a route. */
  cta_href?: string
  text?: string
  html?: string
  title?: string
  description?: string
  image?: string
  src?: string
  alt?: string
}

export interface PageContent {
  page_key: string
  locale: string
  title: string
  blocks: ContentBlock[]
}

export interface GalleryItem {
  id: number
  title: string
  subtitle: string
  filename: string
  alt: string
}

export interface Category {
  id: number
  slug: string
  name: string
  description: string
  /** Slug of the parent category; null for a top-level one. */
  parent_slug: string | null
  product_count: number
}

export interface ProductImage {
  id: number
  filename: string
  alt: string
  is_primary: boolean
}

export interface Product {
  id: number
  slug: string
  name: string
  subtitle: string
  description: string
  year: string
  model: string
  price_cents: number | null
  price_on_request: boolean
  status: ProductStatus
  is_featured: boolean
  category_slug: string | null
  category_name: string | null
  images: ProductImage[]
}

export interface SiteConfig {
  booking_mode: BookingMode
  max_proposed_slots: number
  slot_minutes: number
  slot_buffer_minutes: number
  opening_hours: Record<string, [string, string][]>
  min_notice_hours: number
  max_advance_days: number
  contact: Record<string, string>
  calendar_available: boolean
}

export interface Slot {
  start: string
  end: string
}

export interface Captcha {
  id: string
  question: string
}

export interface SubmitResult {
  ok: boolean
  message: string
  inquiry_id: number | null
}

/** Build a srcset against the backend's pre-generated WebP derivatives. */
export function mediaUrl(filename: string, width?: number): string {
  const name = filename.startsWith('/media/') ? filename.slice('/media/'.length) : filename
  return width ? `/media/${name}?w=${width}` : `/media/${name}`
}

export function mediaSrcSet(filename: string): string {
  return [480, 960, 1920].map((w) => `${mediaUrl(filename, w)} ${w}w`).join(', ')
}

export function formatPrice(product: Product, locale: Locale): string {
  if (product.price_on_request || product.price_cents === null) {
    return t(locale).shop.priceOnRequest
  }
  return new Intl.NumberFormat(INTL_LOCALES[locale], {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(product.price_cents / 100)
}
